import moment from 'moment';
import { SORT_BOOKING_OPTIONS, LAST_MINUTE_MAX_SPOTS } from '../constants';
import { getSelectedFilters } from '../helpers/filter';
import { getTotalStandardPricePerPerson, isBookingOptionInPriceRange } from '../helpers/pricing';
import { getSelectedCrew, getTotalPriceFromSelectedCabins } from '../helpers/week';
import history from '../history';
import { updateSearch } from '../helpers/querystring';
import { store } from '../store/configureStore';

const initialState = {
  items: [],
  filtered: [],
  sortBy: SORT_BOOKING_OPTIONS[0],
  cheapestPrice: null,
  mostExpensivePrice: null
};

export const filterBookingOptions = (state, payload) => {
  const showCalendarFunnel = true;
  const {
    weekFilter = [],
    // destinationFilter = [],
    yearFilter = [],
    month = [],
    cabinBookingType,
    // have to pass these from other reducer states too
    destinations,
    cabins,
    countryFilter,
    festivalFilter,
    packageFilter,
    favourites = null,
    lastMinute = null,
    priceRange,
    hasExplicitPriceFilter = false
  } = payload;
  const weekFilterByMonth = [];
  const results = state.items.filter(item => {
    if (month.length > 0) {
      if (month.includes(`${moment(item.week.beginDate).month()}`)) {
        if (!weekFilterByMonth.includes(item.week.week)) {
          weekFilterByMonth.push(item.week.week);
        }
        // return false;
      }
    }
    if (weekFilter?.length && !weekFilter.includes(`${item.week.week}`)) {
      return false;
    }
    if (festivalFilter?.length && !festivalFilter.includes(`${item.week.id}`)) {
      return false;
    }
    // if (destinationFilter?.length && !destinationFilter.includes(`${item.week.destination.id}`)) {
    //   return false;
    // }
    if (countryFilter?.length && !countryFilter.includes(`${item.week.destination.country}`)) {
      return false;
    }
    if (yearFilter?.length && !yearFilter.includes(new Date(item.week.beginDate).getFullYear().toString())) {
      return false;
    }
    // if (yearFilter?.length && !yearFilter.includes(`${item.week.year}`)) {
    //   return false;
    // }
    if (packageFilter?.length && !packageFilter.includes(`${item?.category?.data.package.id}`)) {
      return false;
    }
    // "Last minute" toggle: keep only package options that are nearly sold out
    // (0 < remaining < LAST_MINUTE_MAX_SPOTS in any booking type). Uses the yacht-list
    // WeekLimit remaining as a scarcity heuristic; custom-yacht options (no package) are excluded.
    if (lastMinute) {
      const packageId = item?.category?.data?.package?.id;
      const availability = packageId != null ? item?.week?.available?.[packageId] : null;
      const isNearlySoldOut = !!availability && ['standard_yacht', 'cabin', 'solo'].some(type => {
        const remaining = availability[type];
        return typeof remaining === 'number' && remaining > 0 && remaining < LAST_MINUTE_MAX_SPOTS;
      });
      if (!isNearlySoldOut) {
        return false;
      }
    }
    if (priceRange?.min != null && priceRange?.max != null) {
      if (!isBookingOptionInPriceRange(item, priceRange, cabins, destinations, history.location, hasExplicitPriceFilter)) {
        return false;
      }
    }
    if (!showCalendarFunnel) {
      if (favourites && !item.isSavedPlan) {
        return false;
      }
      // Only relevant for yacht booking options
      if (!cabinBookingType) {
        if (!item.standardYachtWeek) {
          return false;
        }
      }
      // Only relevant for cabin booking options
      if (cabinBookingType) {
      // 1. handle statuses
        const { isCabinsUnavailable } = item.category;
        if (isCabinsUnavailable) return false;
      }
    }
    return true;
  });
  // clear month from query so won't mess with week filters
  if (month.length > 0) {
    const queryObject = { month: null };
    if (weekFilterByMonth.length) {
      queryObject.weekFilter = weekFilterByMonth;
    }
    history.replace({
      pathname: history.location.pathname,
      search: updateSearch(history.location, queryObject)
    });
  }
  return results;
};

export const sortBookingOptionsReducer = (filteredItems, sortBy, selectedFilters) => {
  const destinations = store.getState().destinations.items;
  const cabins = store.getState().cabins.data;
  const selectedCrew = getSelectedCrew(destinations, history.location);
  const { cabinBookingType } = selectedFilters || getSelectedFilters(history.location);
  let sortedItems = filteredItems;
  switch (sortBy.value) {
  case 1: {
    const cheapestOptionIndex = sortedItems.findIndex(y => y.bestValue);
    if (cheapestOptionIndex > 0) {
      const cheapestOption = sortedItems.splice(cheapestOptionIndex, 1)[0];
      sortedItems.splice(0, 0, cheapestOption);
    }
    break;
  }
  case 2:
    if (cabinBookingType) {
      sortedItems = sortedItems.sort((a, b) => {
        const totalPriceA = getTotalPriceFromSelectedCabins(cabins, destinations, history.location, a.week, a.category?.data.package.id);
        const totalPriceB = getTotalPriceFromSelectedCabins(cabins, destinations, history.location, b.week, b.category?.data.package.id);
        return totalPriceA - totalPriceB;
      });
    } else {
      sortedItems = sortedItems.sort((a, b) =>
        getTotalStandardPricePerPerson(a.standardYachtWeek, a.week, selectedCrew) - getTotalStandardPricePerPerson(b.standardYachtWeek, b.week, selectedCrew));
    }
    break;
  case 3:
    if (cabinBookingType) {
      sortedItems = sortedItems.sort((a, b) => {
        const totalPriceA = getTotalPriceFromSelectedCabins(cabins, destinations, history.location, a.week, a.category?.data.package.id);
        const totalPriceB = getTotalPriceFromSelectedCabins(cabins, destinations, history.location, b.week, b.category?.data.package.id);
        return totalPriceB - totalPriceA;
      });
    } else {
      sortedItems = sortedItems.sort((a, b) =>
        getTotalStandardPricePerPerson(a.standardYachtWeek, a.week, selectedCrew) - getTotalStandardPricePerPerson(b.standardYachtWeek, b.week, selectedCrew));
    }
    break;
  default:
    break;
  }
  return sortedItems;
};


const bookingOptions = (state = initialState, action) => {
  switch (action.type) {
  case 'SAVE_BOOKING_OPTIONS':
    return {
      ...state,
      items: action.bookingOptions,
      filtered: action.bookingOptions,
      cheapestPrice: action.cheapestPrice,
      mostExpensivePrice: action.mostExpensivePrice
    };
  case 'FILTER_BOOKING_OPTIONS': {
    const nextState = {
      ...state,
      filtered: filterBookingOptions(state, action.payload)
    };
    if (action.cheapestPrice != null && action.mostExpensivePrice != null) {
      nextState.cheapestPrice = action.cheapestPrice;
      nextState.mostExpensivePrice = action.mostExpensivePrice;
    }
    return nextState;
  }
  case 'SORT_BOOKING_OPTIONS':
    return {
      ...state,
      // filtered: sortBookingOptions(state, action.payload.sortBy),
      sortBy: action.payload.sortBy
    };
  default:
    return state;
  }
};

export default bookingOptions;
